import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, startOfMonth } from "date-fns";
import api from "../api";

const fmt = n => `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;

const fetchProfitByProduct = (start, end) =>
  api.get("/profit/by-product-v2", { params: { start_date: start, end_date: end } }).then(r => r.data);

export default function ProfitByProduct() {
  const today = new Date();
  const [start, setStart] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(today, "yyyy-MM-dd"));
  const [expanded, setExpanded] = useState({});

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["profit-product", start, end],
    queryFn: () => fetchProfitByProduct(start, end),
  });

  const toggle = (name) => setExpanded(e => ({ ...e, [name]: !e[name] }));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Profit by Product</div>
          <div className="page-subtitle">Revenue and profit grouped by product (FBA + FBM combined)</div>
        </div>
      </div>

      <div className="filters">
        <label style={{ fontSize: "0.85rem", color: "#666" }}>From</label>
        <input type="date" value={start} onChange={e => setStart(e.target.value)} />
        <label style={{ fontSize: "0.85rem", color: "#666" }}>To</label>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)} />
        <button className="btn btn-primary btn-sm" onClick={() => refetch()}>Apply</button>
      </div>

      {isLoading ? <div className="loading">Calculating...</div> : (
        !data?.length ? (
          <div className="empty">No products mapped yet. Go to "Map Products" to group your SKUs.</div>
        ) : (
          <div className="table-card">
            <div className="table-header">
              <span style={{ fontWeight: 600 }}>All Products</span>
              <span style={{ fontSize: "0.8rem", color: "#888" }}>{data.length} products · Click a row to expand SKUs</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Units</th>
                    <th>Revenue</th>
                    <th>FBA Fees</th>
                    <th>COGS</th>
                    <th>Ad Spend</th>
                    <th>Net Profit</th>
                    <th>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map(p => (
                    <React.Fragment key={p.product_name}>
                      {/* Product row */}
                      <tr onClick={() => toggle(p.product_name)}
                        style={{ cursor: "pointer", background: expanded[p.product_name] ? "#f0f4ff" : undefined }}>
                        <td style={{ fontWeight: 600 }}>
                          {expanded[p.product_name] ? "▼" : "▶"} 📦 {p.product_name}
                        </td>
                        <td style={{ fontWeight: 600 }}>{p.total_units}</td>
                        <td style={{ fontWeight: 600 }}>{fmt(p.total_revenue)}</td>
                        <td style={{ color: "#e53e3e" }}>{fmt(p.total_fees)}</td>
                        <td style={{ color: "#dd6b20" }}>{fmt(p.total_cogs)}</td>
                        <td style={{ color: "#805ad5" }}>{fmt(p.allocated_ad_spend || 0)}</td>
                        <td style={{ fontWeight: 700, color: p.total_profit >= 0 ? "#38a169" : "#e53e3e" }}>
                          {fmt(p.total_profit)}
                        </td>
                        <td>
                          <span className={`badge ${p.total_margin >= 20 ? "green" : p.total_margin >= 0 ? "orange" : "red"}`}>
                            {p.total_margin}%
                          </span>
                        </td>
                      </tr>
                      {/* SKU rows (expanded) */}
                      {expanded[p.product_name] && p.skus.map(s => (
                        <tr key={s.sku} style={{ background: "#f8f9ff" }}>
                          <td style={{ paddingLeft: "2.5rem", color: "#666", fontSize: "0.85rem" }}>
                            <code>{s.sku}</code>
                            <span className={`badge ${s.fulfillment_type === "FBA" ? "green" : "orange"}`}
                              style={{ marginLeft: "8px" }}>{s.fulfillment_type}</span>
                          </td>
                          <td style={{ color: "#666" }}>{s.units}</td>
                          <td style={{ color: "#666" }}>{fmt(s.revenue)}</td>
                          <td style={{ color: "#e53e3e" }}>{fmt(s.fees)}</td>
                          <td style={{ color: "#dd6b20" }}>{fmt(s.cogs)}</td>
                          <td style={{ color: s.profit >= 0 ? "#38a169" : "#e53e3e" }}>{fmt(s.profit)}</td>
                          <td><span className={`badge ${s.margin >= 20 ? "green" : s.margin >= 0 ? "orange" : "red"}`}>{s.margin}%</span></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
}
